import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, updateDoc, doc, query, where } from 'firebase/firestore';

const FacilitatorCohortManagement = () => {
  const { currentUser, userProfile } = useAuth();
  const [cohorts, setCohorts] = useState([]);
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [targetCohortId, setTargetCohortId] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [showLearnerDetailModal, setShowLearnerDetailModal] = useState(false);
  const [selectedLearnerDetail, setSelectedLearnerDetail] = useState(null);
  const [loadingLearnerDetail, setLoadingLearnerDetail] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'facilitator') {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      const cohortsCollection = collection(db, 'cohorts');
      const allCohortsSnapshot = await getDocs(cohortsCollection);
      const allCohorts = allCohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const managedCohortIds = new Set([
        ...(Array.isArray(userProfile?.managedCohorts) ? userProfile.managedCohorts : []),
        ...(Array.isArray(userProfile?.assignedCohorts) ? userProfile.assignedCohorts : [])
      ]);
      const managedProgramIds = new Set([
        ...(Array.isArray(userProfile?.assignedPrograms) ? userProfile.assignedPrograms : [])
      ]);

      const cohortsData = allCohorts.filter((cohort) => {
        if (cohort.facilitatorId === currentUser.uid) return true;
        if (managedCohortIds.has(cohort.id)) return true;
        if (cohort.programId && managedProgramIds.has(cohort.programId)) return true;
        return false;
      });
      
      // Load all learners from managed cohorts
      const usersCollection = collection(db, 'users');
      const learnersQuery = query(usersCollection, where('role', '==', 'learner'));
      const learnersSnapshot = await getDocs(learnersQuery);
      const learnersData = learnersSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(learner => 
          cohortsData.some(cohort => cohort.id === learner.cohortId) ||
          (learner.programId && cohortsData.some((cohort) => cohort.programId === learner.programId)) ||
          !learner.cohortId // Include unassigned learners
        );
      
      setCohorts(cohortsData);
      setLearners(learnersData);
      
    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading cohort data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleAssignToCohort = (learner) => {
    setSelectedLearner(learner);
    setTargetCohortId(learner.cohortId || '');
    setAssignReason('');
    setShowAssignModal(true);
  };

  const getLearnerAssignmentSummary = async (learner) => {
    const learnerId = learner?.id;
    if (!learnerId) {
      return {
        submitted: 0,
        outstanding: 0,
        revisionRequested: 0,
        statuses: []
      };
    }

    const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
    const relevantAssignments = assignmentsSnapshot.docs
      .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
      .filter((assignment) => {
        const assignmentCohorts = Array.isArray(assignment.cohortIds)
          ? assignment.cohortIds
          : (assignment.cohortId ? [assignment.cohortId] : []);

        if (assignment.programId && learner.programId && assignment.programId !== learner.programId) {
          return false;
        }
        if (assignmentCohorts.length > 0) {
          return assignmentCohorts.includes(learner.cohortId);
        }
        return true;
      });

    const statuses = relevantAssignments.map((assignment) => {
      const submission = assignment.submissions?.[learnerId];
      const rawStatus = String(submission?.status || '').toLowerCase();
      let status = 'missing';
      if (['completed', 'graded'].includes(rawStatus)) status = 'submitted';
      else if (rawStatus === 'revision_requested') status = 'revision_requested';
      else if (rawStatus) status = rawStatus;

      return {
        title: assignment.title || 'Assignment',
        status
      };
    });

    return {
      submitted: statuses.filter((item) => ['submitted', 'graded', 'completed'].includes(item.status)).length,
      outstanding: statuses.filter((item) => item.status === 'missing').length,
      revisionRequested: statuses.filter((item) => item.status === 'revision_requested').length,
      statuses
    };
  };

  const openLearnerDetail = async (learner) => {
    setSelectedLearnerDetail(null);
    setShowLearnerDetailModal(true);
    setLoadingLearnerDetail(true);

    try {
      const [documentsSnapshot, attendanceSnapshot, assignmentSummary] = await Promise.all([
        getDocs(collection(db, 'users', learner.id, 'documents')),
        getDocs(query(collection(db, 'attendance'), where('learnerId', '==', learner.id))),
        getLearnerAssignmentSummary(learner)
      ]);

      const documents = documentsSnapshot.docs
        .map((docSnap) => docSnap.data())
        .filter((item) => item?.deleted !== true);
      const attendanceRecords = attendanceSnapshot.docs.map((docSnap) => docSnap.data());
      const presentCount = attendanceRecords.filter((record) => record.status === 'present').length;
      const totalAttendance = attendanceRecords.length;
      const attendanceRate = totalAttendance > 0
        ? Math.round((presentCount / totalAttendance) * 100)
        : 0;

      const submittedDocuments = documents.filter((item) => item.status && item.status !== 'missing');
      const rejectedDocuments = documents.filter((item) => item.reviewStatus === 'qa_rejected' || item.status === 'rejected');

      setSelectedLearnerDetail({
        learner,
        documents: {
          submitted: submittedDocuments.length,
          rejected: rejectedDocuments.length,
          items: documents.map((item) => ({
            category: item.category || item.categoryId || item.documentType || 'Document',
            status: item.reviewStatus || item.status || 'pending'
          }))
        },
        attendance: {
          total: totalAttendance,
          present: presentCount,
          rate: attendanceRate
        },
        assignments: assignmentSummary
      });
    } catch (error) {
      devError('Error loading learner detail:', error);
      showAlert('Failed to load learner detail.', 'danger');
      setShowLearnerDetailModal(false);
    } finally {
      setLoadingLearnerDetail(false);
    }
  };

  const handleAssignConfirm = async () => {
    if (!selectedLearner || !targetCohortId) {
      showAlert('Please select a cohort', 'warning');
      return;
    }

    try {
      const userRef = doc(db, 'users', selectedLearner.id);
      const updatedData = {
        cohortId: targetCohortId,
        updatedAt: new Date(),
        lastModifiedBy: currentUser.uid
      };

      // Add to cohort assignment history
      if (selectedLearner.cohortAssignmentHistory) {
        updatedData.cohortAssignmentHistory = [
          ...selectedLearner.cohortAssignmentHistory,
          {
            fromCohortId: selectedLearner.cohortId || null,
            toCohortId: targetCohortId,
            assignedBy: currentUser.uid,
            assignedAt: new Date(),
            reason: assignReason || 'Cohort assignment by facilitator'
          }
        ];
      } else {
        updatedData.cohortAssignmentHistory = [{
          fromCohortId: selectedLearner.cohortId || null,
          toCohortId: targetCohortId,
          assignedBy: currentUser.uid,
          assignedAt: new Date(),
          reason: assignReason || 'Initial cohort assignment by facilitator'
        }];
      }

      await updateDoc(userRef, updatedData);
      
      // Update local state
      setLearners(prev => prev.map(learner => 
        learner.id === selectedLearner.id 
          ? { ...learner, cohortId: targetCohortId, cohortAssignmentHistory: updatedData.cohortAssignmentHistory }
          : learner
      ));
      
      setShowAssignModal(false);
      showAlert(`Successfully assigned ${selectedLearner.displayName} to cohort`, 'success');
      
    } catch (error) {
      devError('Error assigning learner to cohort:', error);
      showAlert('Error assigning learner to cohort', 'danger');
    }
  };

  const getCohortName = (cohortId) => {
    const cohort = cohorts.find(c => c.id === cohortId);
    return cohort ? cohort.name : 'Unassigned';
  };

  const isActiveLearner = (learner) => {
    if (!learner) return false;
    if (learner.deleted || learner.deactivated) return false;
    if (learner.isActive === false) return false;
    const status = (learner.status || '').toLowerCase();
    if (['inactive', 'dropoff', 'dropped'].includes(status)) return false;
    return true;
  };

  const getLearnersInCohort = (cohortId) => {
    return learners.filter(learner => learner.cohortId === cohortId);
  };

  const getActiveLearnersInCohort = (cohortId) => {
    return learners.filter(learner => learner.cohortId === cohortId && isActiveLearner(learner));
  };

  const getInactiveLearnersInCohort = (cohortId) => {
    return learners.filter(learner => learner.cohortId === cohortId && !isActiveLearner(learner));
  };

  const getUnassignedLearners = () => {
    return learners.filter(learner => !learner.cohortId);
  };

  if (userProfile?.role !== 'facilitator') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access cohort management.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading cohort data...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {alert.show && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false, message: '', type: '' })}>
          {alert.message}
        </Alert>
      )}

      <Row className="mb-4">
        <Col>
          <h2>Cohort Management</h2>
          <p className="text-muted">Assign learners to cohorts you manage</p>
        </Col>
      </Row>

      {/* Unassigned Learners */}
      {getUnassignedLearners().length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">📋 Unassigned Learners ({getUnassignedLearners().length})</h5>
              </Card.Header>
              <Card.Body>
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Last Sign-In</th>
                      <th>Enrolled Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getUnassignedLearners().map(learner => {
                      // Format last login time
                      const formatLastLogin = (lastLoginAt, createdAt) => {
                        if (!lastLoginAt) return { text: 'Never', variant: 'secondary' };

                        const lastLogin = lastLoginAt.toDate ? lastLoginAt.toDate() : new Date(lastLoginAt);

                        // Check if lastLoginAt is the same as createdAt (never actually logged in)
                        if (createdAt) {
                          const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
                          const timeDiff = Math.abs(lastLogin - created);
                          if (timeDiff < 5000) {
                            return { text: 'Never', variant: 'secondary' };
                          }
                        }

                        const now = new Date();
                        const diffMs = now - lastLogin;
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffMinutes = Math.floor(diffMs / (1000 * 60));

                        if (diffMinutes < 60) return { text: `${diffMinutes}m ago`, variant: 'success' };
                        if (diffHours < 24) return { text: `${diffHours}h ago`, variant: 'success' };
                        if (diffDays === 1) return { text: 'Yesterday', variant: 'info' };
                        if (diffDays < 7) return { text: `${diffDays}d ago`, variant: 'info' };
                        if (diffDays < 30) return { text: `${diffDays}d ago`, variant: 'warning' };
                        return { text: `${diffDays}d ago`, variant: 'danger' };
                      };

                      const lastLoginInfo = formatLastLogin(learner.lastLoginAt, learner.createdAt);

                      return (
                        <tr key={learner.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div
                                className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                                style={{ width: '32px', height: '32px', fontSize: '14px' }}
                              >
                                {learner.displayName.charAt(0)}
                              </div>
                              <strong>{learner.displayName}</strong>
                            </div>
                          </td>
                          <td>{learner.email}</td>
                          <td>
                            <Badge bg={learner.status === 'active' ? 'success' : 'warning'}>
                              {learner.status || 'active'}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg={lastLoginInfo.variant}>
                              {lastLoginInfo.text}
                            </Badge>
                          </td>
                          <td>{learner.enrolledDate ? new Date(learner.enrolledDate).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => openLearnerDetail(learner)}
                              >
                                View Details
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleAssignToCohort(learner)}
                              >
                                Assign to Cohort
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Cohorts */}
      <Row>
        {cohorts.map(cohort => (
          <Col md={6} lg={4} key={cohort.id} className="mb-4">
            <Card className="h-100">
              <Card.Header>
                <h5 className="mb-1">{cohort.name}</h5>
                <small className="text-muted">{cohort.description}</small>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Enrollment:</span>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge bg="info">{getActiveLearnersInCohort(cohort.id).length}/{cohort.capacity || 30}</Badge>
                      <Badge bg="secondary">{getInactiveLearnersInCohort(cohort.id).length} inactive</Badge>
                    </div>
                  </div>
                  <div className="progress">
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${Math.min(100, (getActiveLearnersInCohort(cohort.id).length / (cohort.capacity || 30)) * 100)}%` }}
                    />
                  </div>
                </div>
                
                {getLearnersInCohort(cohort.id).length > 0 ? (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {getLearnersInCohort(cohort.id).map(learner => {
                      // Format last login time
                      const formatLastLogin = (lastLoginAt, createdAt) => {
                        if (!lastLoginAt) return { text: 'Never', variant: 'secondary' };

                        const lastLogin = lastLoginAt.toDate ? lastLoginAt.toDate() : new Date(lastLoginAt);

                        // Check if lastLoginAt is the same as createdAt (never actually logged in)
                        if (createdAt) {
                          const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
                          const timeDiff = Math.abs(lastLogin - created);
                          if (timeDiff < 5000) {
                            return { text: 'Never', variant: 'secondary' };
                          }
                        }

                        const now = new Date();
                        const diffMs = now - lastLogin;
                        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffMinutes = Math.floor(diffMs / (1000 * 60));

                        if (diffMinutes < 60) return { text: `${diffMinutes}m ago`, variant: 'success' };
                        if (diffHours < 24) return { text: `${diffHours}h ago`, variant: 'success' };
                        if (diffDays === 1) return { text: 'Yesterday', variant: 'info' };
                        if (diffDays < 7) return { text: `${diffDays}d ago`, variant: 'info' };
                        if (diffDays < 30) return { text: `${diffDays}d ago`, variant: 'warning' };
                        return { text: `${diffDays}d ago`, variant: 'danger' };
                      };

                      const lastLoginInfo = formatLastLogin(learner.lastLoginAt, learner.createdAt);

                      return (
                        <div key={learner.id} className="d-flex justify-content-between align-items-center mb-1 p-1">
                          <div>
                            <small className="fw-bold">{learner.displayName}</small>
                            <br />
                            <small className="text-muted">{learner.email}</small>
                            <br />
                            <Badge bg={lastLoginInfo.variant} className="mt-1" style={{ fontSize: '0.7rem' }}>
                              {lastLoginInfo.text}
                            </Badge>
                          </div>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => openLearnerDetail(learner)}
                            title="View learner detail"
                          >
                            👁️
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleAssignToCohort(learner)}
                            title="Reassign to different cohort"
                          >
                            ⚙️
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted text-center">No learners assigned</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showLearnerDetailModal} onHide={() => setShowLearnerDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Learner Progress Overview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingLearnerDetail ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">Loading learner detail...</div>
            </div>
          ) : selectedLearnerDetail ? (
            <>
              <Alert variant="light">
                <div><strong>Name:</strong> {selectedLearnerDetail.learner.displayName}</div>
                <div><strong>Email:</strong> {selectedLearnerDetail.learner.email || 'N/A'}</div>
                <div><strong>Phone:</strong> {selectedLearnerDetail.learner.phone || selectedLearnerDetail.learner.phoneNumber || 'N/A'}</div>
                <div><strong>Cohort:</strong> {getCohortName(selectedLearnerDetail.learner.cohortId)}</div>
              </Alert>

              <Row className="mb-3">
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h4 className="text-primary">{selectedLearnerDetail.assignments.submitted}</h4>
                      <small className="text-muted">Assignments Submitted</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h4 className="text-warning">{selectedLearnerDetail.documents.submitted}</h4>
                      <small className="text-muted">Compliance Documents Submitted</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h4 className="text-success">{selectedLearnerDetail.attendance.rate}%</h4>
                      <small className="text-muted">Attendance Rate</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <h6>Outstanding Items</h6>
              <ul className="mb-3">
                <li>{selectedLearnerDetail.assignments.outstanding} assignment(s) outstanding</li>
                <li>{selectedLearnerDetail.assignments.revisionRequested} assignment(s) require revision</li>
                <li>{selectedLearnerDetail.documents.rejected} document(s) rejected / needing action</li>
                <li>{Math.max(selectedLearnerDetail.attendance.total - selectedLearnerDetail.attendance.present, 0)} attendance record(s) not marked present</li>
              </ul>

              <h6>Assignment Status</h6>
              <Table striped bordered hover responsive size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th>Assignment</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLearnerDetail.assignments.statuses.map((item, index) => (
                    <tr key={`${item.title}-${index}`}>
                      <td>{item.title}</td>
                      <td><Badge bg={
                        item.status === 'submitted' ? 'success' :
                          item.status === 'revision_requested' ? 'warning' :
                            item.status === 'missing' ? 'secondary' : 'info'
                      }>{item.status.replace(/_/g, ' ')}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <h6>Compliance Documents</h6>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLearnerDetail.documents.items.map((item, index) => (
                    <tr key={`${item.category}-${index}`}>
                      <td>{item.category}</td>
                      <td>{item.status}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          {selectedLearnerDetail?.learner?.email && (
            <Button
              variant="outline-primary"
              onClick={() => window.open(`mailto:${selectedLearnerDetail.learner.email}`, '_blank')}
            >
              Email Learner
            </Button>
          )}
          <Button variant="secondary" onClick={() => setShowLearnerDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assignment Modal */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Learner to Cohort</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLearner && (
            <>
              <Alert variant="info">
                <strong>Learner:</strong> {selectedLearner.displayName} ({selectedLearner.email})
                <br />
                <strong>Current Cohort:</strong> {getCohortName(selectedLearner.cohortId)}
              </Alert>
              
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Target Cohort</Form.Label>
                  <Form.Control
                    as="select"
                    value={targetCohortId}
                    onChange={(e) => setTargetCohortId(e.target.value)}
                  >
                    <option value="">Select Cohort</option>
                    {cohorts.map(cohort => (
                      <option key={cohort.id} value={cohort.id}>
                        {cohort.name} ({getLearnersInCohort(cohort.id).length}/{cohort.capacity || 30})
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Reason for Assignment</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={assignReason}
                    onChange={(e) => setAssignReason(e.target.value)}
                    placeholder="Optional reason for this assignment..."
                  />
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignConfirm}
            disabled={!targetCohortId}
          >
            Assign to Cohort
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FacilitatorCohortManagement;
